import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/App";
import Navbar from "@/components/Navbar";
import { Sparkles, Shield, Check } from "lucide-react";

/**
 * Shareable registration entry point. Visiting /join (or /register) immediately
 * kicks the visitor into the Google OAuth flow and lands them on the dashboard
 * with the "create business" form ready to go. If they're already logged in
 * we send them straight to the dashboard.
 */
export default function JoinPage() {
  const { user, loading, login } = useAuth();

  // If they're already signed in, send them straight to dashboard.
  if (!loading && user) {
    return <Navigate to="/dashboard?new=business" replace />;
  }

  return (
    <div className="min-h-screen bg-tbc-cream grain-texture flex flex-col" data-testid="join-page">
      <Navbar />
      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="max-w-xl w-full bg-white rounded-2xl shadow-lg border border-gray-100 p-8 sm:p-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-tbc-gold/15 text-tbc-navy rounded-full text-[11px] uppercase tracking-[0.18em] font-medium mb-6">
            <Sparkles className="w-3.5 h-3.5 text-tbc-gold" />
            Founding Members Welcome
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl text-tbc-navy mb-3" data-testid="join-heading">
            List your Black-owned business
          </h1>
          <p className="text-gray-600 font-body mb-8">
            Sign in with Google to claim your free founding spot. Takes about 30 seconds.
          </p>

          <ul className="text-left space-y-2.5 mb-8 text-sm text-gray-700 font-body">
            {[
              "Pre-launch sale — limited-time founding offer",
              "Only 100 Founding spots — first come, first served",
              "Free publicity during the pre-launch phase",
              "First 30 days of paid features free at official launch",
              "$9.99/month founding rate locked in for life",
              "Founding Member badge + priority placement",
            ].map((b) => (
              <li key={b} className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full bg-tbc-gold/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-tbc-gold" />
                </span>
                {b}
              </li>
            ))}
          </ul>

          <button
            onClick={() => login("/dashboard?new=business")}
            className="w-full bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-8 py-4 font-medium text-base transition-colors"
            data-testid="join-google-button"
          >
            Continue with Google
          </button>

          <p className="mt-6 text-xs text-gray-500 font-body inline-flex items-center gap-1.5">
            <Shield className="w-3 h-3" /> Your information is private. We&apos;ll never share or sell it.
          </p>

          <p className="mt-8 text-xs text-gray-400 font-body">
            Already a member?{" "}
            <Link to="/dashboard" className="text-tbc-navy underline hover:text-tbc-gold">
              Go to your dashboard
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
